
import { Progress, SignInProviderConfig, UserIdentity } from '@backstage/core-components';
import { BackstageIdentityResponse, SignInPageProps, useApi } from '@backstage/core-plugin-api';
import { useMountEffect } from '@react-hookz/web';
import React, { useState } from 'react';
import { SlackIcon } from './../components/icons';
import { TotvsLogoFull } from './../themes';
import { Button, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';

type SingleSignInPageProps = SignInPageProps & {
    provider: SignInProviderConfig;
    icon: React.ReactNode;
    auto?: boolean;
};

export type Props = SingleSignInPageProps;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            overflowY: 'auto',
            height: '100vh',
            [theme.breakpoints.down('xs')]: {
                height: '100%',
            },
            backgroundColor: theme.palette.background.paper
        },
        container: {
            height: '100vh',
            justifyContent: 'center',
            alignItems: 'center'
        },
        loginButton: {
            color: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.main
        },
        helpText: {
            lineHeight: '30px'
        },
        helpLink: {
            fontSize: '0.65rem'
        },
        imageBackgroud: {
            backgroundImage: 'url("/login-background.jpg")',
            backgroundPosition: '50%  50%',
            backgroundSize: 'cover',
            filter: 'drop-shadow(0 1em 0.5em rgba(0,0,0,0.32))'
        }
    })
);

export const TotvsSignInPage = ({ provider, icon, auto, onSignInSuccess }: SingleSignInPageProps) => {

    const authApi = useApi(provider.apiRef);
    const classes = useStyles();

    // The SignIn component takes some time to decide whether the user is logged-in or not.
    // showLoginPage is used to prevent a glitch-like experience where the sign-in page is
    // displayed for a split second when the user is already logged-in.
    const [showLoginPage, setShowLoginPage] = useState<boolean>(false);

    type LoginOpts = { checkExisting?: boolean; showPopup?: boolean };

    const login = async ({ checkExisting, showPopup }: LoginOpts) => {
        try {
            let identityResponse: BackstageIdentityResponse | undefined;
            if (checkExisting) {
                // Do an initial check if any logged-in session exists
                identityResponse = await authApi.getBackstageIdentity({
                    optional: true,
                });
            }

            // If no session exists, show the sign-in page
            if (!identityResponse && (showPopup || auto)) {
                // Unless auto is set to true, this step should not happen.
                // When user intentionally clicks the Sign In button, autoShowPopup is set to true
                setShowLoginPage(true);
                identityResponse = await authApi.getBackstageIdentity({
                    instantPopup: true,
                });
                if (!identityResponse) {
                    throw new Error(
                        `The ${provider.title} provider is not configured to support sign-in`,
                    );
                }
            }

            if (!identityResponse) {
                setShowLoginPage(true);
                return;
            }

            const profile = await authApi.getProfile();
            onSignInSuccess(
                UserIdentity.create({
                    identity: identityResponse.identity,
                    authApi,
                    profile,
                }),
            );

        } catch (err: any) {
            setShowLoginPage(true);
        }
    };

    useMountEffect(() => login({ checkExisting: true }));

    return showLoginPage ? (
        <Grid container spacing={0} className={classes.root}>
            <Grid item sm={6} xs>
                <Grid container  direction='column' className={classes.container}>
                    <TotvsLogoFull height={60} />
                    <br />
                    <Button variant='outlined' size='large'
                        startIcon={icon}
                        className={classes.loginButton}
                        onClick={() => {
                            login({ showPopup: true });
                        }}
                    >
                        {provider.message}
                    </Button>
                    <br />
                    <Typography variant='subtitle2' className={classes.helpText}>
                        If you are unable to sign in, please contact us: &nbsp;&nbsp;
                        <Button className={classes.helpLink} href="https://totvsapps.slack.com/archives/C07794LF5RP" startIcon={<SlackIcon />}>
                            supply-saas
                        </Button>
                    </Typography>
                </Grid>
            </Grid>
            <Grid sm={6} xs className={classes.imageBackgroud} />
        </Grid>
    ) : (
        <Progress />
    );
};
